import React from 'react'

import {FormikProps} from 'formik'
import invariant from 'invariant'
import difference from 'lodash/difference'
import flatMap from 'lodash/flatMap'
import intersectionBy from 'lodash/intersectionBy'
import isEmpty from 'lodash/isEmpty'
import * as Yup from 'yup'

import {useAppDispatch, useAppSelector} from '../../hooks/redux'
import {openCreateInfillModal} from '../../redux/features/modal'

import {formatMillimeters, formatTimeRange} from '../../libs/formatter'
import * as providerInfoOptionApi from '../../services/api/provider-info-option'
import {PossiblyMixedValue, isMixedValue, toPossiblyMixedValue} from '../utils/form'

import {
  Nullable,
  Option,
  OptionId,
  OptionWithLinkedProviderInfos,
  PrintingMethod,
  PrintingMethodId,
  ProviderInfo,
  ProviderInfoId,
} from '../../types'

import Chip from '../../../../commons/src/components/chip'
import DescriptionList from '../../../../commons/src/components/description-list'
import DescriptionListItem from '../../../../commons/src/components/description-list/DescriptionListItem'
import FieldNote from '../../../../commons/src/components/field-note'
import FormRow from '../../../../commons/src/components/form-row'
import Headline from '../../../../commons/src/components/headline'
import Link from '../../../../commons/src/components/link'
import Paragraph from '../../../../commons/src/components/paragraph'
import SelectField from '../../../../commons/src/components/select-field'
import SelectMenu from '../../../../commons/src/components/select-menu'
import Stack from '../../../../commons/src/components/stack'

import MultiSelectFieldPartial from './multi-select-field-partial'

export type SingleFormData = {
  printingMethodId: Nullable<string>
  infillIds: OptionId[]
  formikGeneral?: unknown // Need type to be able to set general validation error.
}

export type MixedFormData = {
  printingMethodId: PossiblyMixedValue<string>
  infillIds: PossiblyMixedValue<OptionId[]>
  formikGeneral?: unknown // Need type to be able to set general validation error.
}

export type PrintingMethodOption = {
  label: string
  value: PrintingMethodId
}

type Props = {
  formik: FormikProps<SingleFormData> | FormikProps<MixedFormData>
  onRefresh: () => void
  submitButton?: React.ReactNode
  isLoading: boolean
  infillOptions: PrintingMethodOption[]
  providerInfos: ProviderInfo[]
}

export const initialValues = {
  printingMethodId: '',
  infillIds: [],
}

export const validationSchema = {
  printingMethodId: Yup.string().label('Printing method').required(),
}

export const printingMethodToMixedFormData = (
  providerInfos: ProviderInfo[],
  infills: OptionWithLinkedProviderInfos[]
): MixedFormData => ({
  printingMethodId: toPossiblyMixedValue(
    providerInfos.map(providerInfo => providerInfo.printingMethodId),
    id => id || ''
  ),
  infillIds: toPossiblyMixedValue(
    providerInfos.map(providerInfo =>
      infills
        .filter(({providerInfos: infillProviderInfos}) =>
          infillProviderInfos.map(({id}) => id).includes(providerInfo.id)
        )
        .map(({id}) => id)
    ),
    ids => ids
  ),
})

export const formDataToPartialRequest = (formData: Partial<SingleFormData>) => ({
  printingMethodId: formData.printingMethodId,
})

export const formatInfillName = (infill: OptionWithLinkedProviderInfos) => {
  const name = infill.name
  const percentage = `${parseInt(infill.type.replace('infill_', ''), 10)}%`
  const days = formatTimeRange(
    infill.productionTimeFastOffset || undefined,
    infill.productionTimeSlowOffset || undefined
  )

  return name ? `${name} (${percentage}, +${days})` : `${percentage}, +${days}`
}

export const sortInfills = (infills: OptionWithLinkedProviderInfos[]) =>
  infills.sort((a, b) =>
    formatInfillName(a).localeCompare(formatInfillName(b), undefined, {
      numeric: true,
    })
  )

export const updateLinkedInfills = (
  initialInfillids: OptionId[],
  nextInfillIds: OptionId[],
  providerInfoIds: ProviderInfoId[]
) => {
  const addedInfillIds = difference(nextInfillIds, initialInfillids)
  const removedInfillIds = difference(initialInfillids, nextInfillIds)

  // Unlink providerInfos from removed infill options
  const unlinkPromises = flatMap(removedInfillIds, optionId =>
    providerInfoIds.map(providerInfoId =>
      providerInfoOptionApi.remove({
        providerInfoId,
        optionId,
      })
    )
  )

  // Add provider Infos to newly added infill options
  const linkPromises = flatMap(addedInfillIds, optionId =>
    providerInfoIds.map(providerInfoId =>
      providerInfoOptionApi.create({
        providerInfoId,
        optionId,
      })
    )
  )

  return Promise.all([...unlinkPromises, ...linkPromises])
}

const PrintingMethodFormPartial = ({
  formik,
  onRefresh,
  submitButton,
  isLoading,
  infillOptions,
  providerInfos,
}: Props) => {
  const dispatch = useAppDispatch()
  const materials = useAppSelector(state => state.core.materials)
  const finishGroups = useAppSelector(state => state.core.finishGroups)
  const materialConfigs = useAppSelector(state => state.core.materialConfigs)
  const printingMethods = useAppSelector(state => state.core.printingMethods)

  const providerInfosPrintingMethods = intersectionBy(
    ...providerInfos.reduce(
      (acc, providerInfo) => [
        ...acc,
        materials[
          finishGroups[materialConfigs[providerInfo.materialConfigId].finishGroupId].materialId
        ].printingMethods,
      ],
      [] as PrintingMethod[][]
    ),
    'id'
  )

  invariant(providerInfosPrintingMethods, 'Printing methods missing.')

  const printingMethodOptions: PrintingMethodOption[] = Object.values(
    providerInfosPrintingMethods
  ).map(printingMethod => ({
    label: printingMethod.name,
    value: printingMethod.id,
  }))

  const getPrintingMethodOptionById = (printingMethodId: Nullable<PrintingMethodId>) =>
    printingMethodOptions.find(({value}) => value === printingMethodId)

  const setCreatedInfill = (infill: Option) => {
    formik.setFieldValue('infillIds', [...(formik.values.infillIds as OptionId[]), infill.id])
    onRefresh()
  }

  const isPrintingMethodFDM = (printingMethodId: Nullable<PrintingMethodId>) =>
    getPrintingMethodOptionById(printingMethodId)?.label === 'FDM'

  const setPrintingMethodOption = (printingMethodOption: PrintingMethodOption) => {
    // Clear infills when switching the printing method away from FDM
    if (!isPrintingMethodFDM(printingMethodOption.value) && !isEmpty(formik.values.infillIds)) {
      formik.setFieldValue('infillIds', [])
    }

    formik.setFieldValue('printingMethodId', printingMethodOption.value)
  }

  const renderPrintingMethodDetails = () => {
    const printingMethodId = formik.values.printingMethodId
    if (!printingMethodId || isMixedValue(printingMethodId)) return null

    const printingMethod = printingMethods[printingMethodId]

    if (!printingMethod.clearance && !printingMethod.minDetails && !printingMethod.minWallThickness)
      return null

    return (
      <Stack>
        <Headline size="s" label="Details" className="u-no-margin-bottom" />
        <Paragraph size="s">
          <DescriptionList>
            {printingMethod.clearance && (
              <DescriptionListItem label="Clearance">
                {printingMethod.clearance}
              </DescriptionListItem>
            )}
            {printingMethod.minDetails && (
              <DescriptionListItem label="Min. details">
                {formatMillimeters(printingMethod.minDetails)}
              </DescriptionListItem>
            )}
            {printingMethod.minWallThickness && (
              <DescriptionListItem label="Min. wall thickness">
                {formatMillimeters(printingMethod.minWallThickness)}
              </DescriptionListItem>
            )}
          </DescriptionList>
        </Paragraph>
      </Stack>
    )
  }

  return (
    <form onSubmit={formik.handleSubmit}>
      {formik.errors.formikGeneral && <Paragraph error>{formik.errors.formikGeneral}</Paragraph>}
      <Stack space="300">
        <FormRow>
          <FieldNote
            appearance={
              formik.submitCount > 0 && formik.errors.printingMethodId ? 'error' : 'warning'
            }
            note={
              formik.submitCount > 0 && formik.errors.printingMethodId
                ? formik.errors.printingMethodId
                : printingMethodOptions.length === 0
                  ? 'There are no selectable shared values for the combination of configurations you selected.'
                  : null
            }
          >
            <SelectField
              name="printingMethodId"
              placeholder="Choose printing method"
              disabled={printingMethodOptions.length === 0}
              mixedLabel="Mixed"
              mixed={isMixedValue(formik.values.printingMethodId)}
              value={
                !isMixedValue(formik.values.printingMethodId)
                  ? getPrintingMethodOptionById(formik.values.printingMethodId)
                  : getPrintingMethodOptionById(initialValues.printingMethodId)
              }
              menu={<SelectMenu values={printingMethodOptions} />}
              onChange={printingMethodOption => {
                setPrintingMethodOption(printingMethodOption as PrintingMethodOption)
              }}
              onBlur={formik.handleBlur}
            />
          </FieldNote>
        </FormRow>
        {renderPrintingMethodDetails()}
        {!isMixedValue(formik.initialValues.printingMethodId) &&
          isPrintingMethodFDM(formik.initialValues.printingMethodId) && (
            <div>
              <Headline size="s" label="Infill options" className="u-margin-bottom" />
              {!isLoading && !isMixedValue(formik.values.infillIds) && (
                <Paragraph>
                  You can either select one or more of your existing infill options or you can{' '}
                  <Link onClick={() => dispatch(openCreateInfillModal({onDone: setCreatedInfill}))}>
                    create a new one
                  </Link>
                  .
                </Paragraph>
              )}
              <FormRow className="u-no-margin-bottom">
                <MultiSelectFieldPartial
                  isLoading={isLoading}
                  selectLabel="Link infill option"
                  defaultOption={<Chip label="Default (20%)" />}
                  options={infillOptions}
                  values={formik.values.infillIds}
                  onChange={(values: OptionId[]) => {
                    formik.setFieldValue('infillIds', values)
                  }}
                  onReset={() => {
                    formik.setFieldValue('infillIds', initialValues.infillIds)
                  }}
                />
              </FormRow>
            </div>
          )}
        {submitButton}
      </Stack>
    </form>
  )
}

export default PrintingMethodFormPartial
